import { useState, useEffect, useRef, useMemo } from 'react';
import { CKEditor, useCKEditorCloud } from '@ckeditor/ckeditor5-react';
import { useField } from 'formik';
import { translate } from '../basic/text';
import { useT } from '../../i18n';
import { code } from '../basic/code';

const LICENSE_KEY = code.isDevelopmentEnv()
    ? 'eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE3Nzk0OTQzOTksImp0aSI6Ijc1NGFkYTBjLTFiOTktNGVjYy1hZTZjLTk5MmY3YmMwNWY1OCIsImxpY2Vuc2VkSG9zdHMiOlsiMTI3LjAuMC4xIiwibG9jYWxob3N0IiwiMTkyLjE2OC4qLioiLCIxMC4qLiouKiIsIjE3Mi4qLiouKiIsIioudGVzdCIsIioubG9jYWxob3N0IiwiKi5sb2NhbCJdLCJ1c2FnZUVuZHBvaW50IjoiaHR0cHM6Ly9wcm94eS1ldmVudC5ja2VkaXRvci5jb20iLCJkaXN0cmlidXRpb25DaGFubmVsIjpbImNsb3VkIiwiZHJ1cGFsIl0sImxpY2Vuc2VUeXBlIjoiZGV2ZWxvcG1lbnQiLCJmZWF0dXJlcyI6WyJEUlVQIiwiRTJQIiwiRTJXIl0sInZjIjoiOTNlZTczYjkifQ.LDJDreyLhokUQy3xVy6Crp0HehU0rguMl7790NFoCL8Xh2f5OuBl_HMeOAgFs3ohQeaCRiUc6i_TeECv6kOgGQ'
    : 'eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE3ODA0NDQ3OTksImp0aSI6IjVlNmUxMjFmLTA1MjMtNDYzZi1iZWRkLTY5MDliMjE2ZDEyZSIsImxpY2Vuc2VkSG9zdHMiOlsiYWRtaW4uZzJvLmlyIl0sInVzYWdlRW5kcG9pbnQiOiJodHRwczovL3Byb3h5LWV2ZW50LmNrZWRpdG9yLmNvbSIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsiY2xvdWQiLCJkcnVwYWwiXSwiZmVhdHVyZXMiOlsiRFJVUCIsIkUyUCIsIkUyVyJdLCJ2YyI6IjRjM2RmMGFkIn0.g0DFiXTAWE8mLn0X7QcPX61YS8LdetrasOgW9VmOVEUUwjtrUAmE_xDzArjUEE0nH3WTFc9xJcIZ2CJb0msysw';

export default function FormikTextEditor({ content, ...props }: { content: string } & any) {
    const [field, meta, helpers] = useField(props as any);
    const { t } = useT();
    const displayValue = field.value;
    const cloud = useCKEditorCloud({ version: '45.1.0' });

    const onChangeHandler = (event: any, editor: any) => {
        const data = editor.getData();
        field.onChange({ target: { name: field.name, value: data } });
        if (props.onChange) props.onChange(data);
    };

    meta.error = translate(meta.error, null, t);

    const editorContainerRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<HTMLDivElement>(null);
    const [isLayoutReady, setIsLayoutReady] = useState(false);

    useEffect(() => {
        setIsLayoutReady(true);
        return () => setIsLayoutReady(false);
    }, []);

    const { ClassicEditor, editorConfig } = useMemo(() => {
        if (cloud.status !== 'success' || !isLayoutReady) {
            return {};
        }

        const {
            ClassicEditor,
            Autosave,
            BalloonToolbar,
            BlockQuote,
            Bold,
            Essentials,
            Heading,
            Indent,
            IndentBlock,
            Italic,
            Link,
            Paragraph,
            Table,
            TableCaption,
            TableCellProperties,
            TableColumnResize,
            TableProperties,
            TableToolbar,
            Underline,
        } = cloud.CKEditor;

        return {
            ClassicEditor,
            editorConfig: {
                toolbar: {
                    items: [
                        'undo',
                        'redo',
                        '|',
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        '|',
                        'link',
                        'insertTable',
                        'blockQuote',
                        '|',
                        'outdent',
                        'indent',
                    ],
                    shouldNotGroupWhenFull: false,
                },
                plugins: [
                    Autosave,
                    BalloonToolbar,
                    BlockQuote,
                    Bold,
                    Essentials,
                    Heading,
                    Indent,
                    IndentBlock,
                    Italic,
                    Link,
                    Paragraph,
                    Table,
                    TableCaption,
                    TableCellProperties,
                    TableColumnResize,
                    TableProperties,
                    TableToolbar,
                    Underline,
                ],
                balloonToolbar: ['bold', 'italic', '|', 'link'],
                heading: {
                    options: [
                        {
                            model: 'paragraph' as 'paragraph',
                            view: 'p',
                            title: 'Paragraph',
                            class: 'ck-heading_paragraph',
                        },
                        {
                            model: 'heading1' as 'heading1',
                            view: 'h1',
                            title: 'Heading 1',
                            class: 'ck-heading_heading1',
                        },
                        {
                            model: 'heading2' as 'heading2',
                            view: 'h2',
                            title: 'Heading 2',
                            class: 'ck-heading_heading2',
                        },
                        {
                            model: 'heading3' as 'heading3',
                            view: 'h3',
                            title: 'Heading 3',
                            class: 'ck-heading_heading3',
                        },
                    ],
                },
                initialData: content || '',
                licenseKey: LICENSE_KEY,
                placeholder: 'Type or paste your content here!',
                table: {
                    contentToolbar: [
                        'tableColumn',
                        'tableRow',
                        'mergeTableCells',
                        'tableProperties',
                        'tableCellProperties',
                    ],
                },
            },
        };
    }, [cloud, isLayoutReady, content]);

    return (
        <div className="w-100 h-100">
            <div
                className="editor-container editor-container_classic-editor"
                ref={editorContainerRef}
            >
                <div ref={editorRef}>
                    {ClassicEditor && editorConfig && (
                        <CKEditor
                            editor={ClassicEditor}
                            config={editorConfig}
                            data={displayValue}
                            onError={err => {
                                helpers.setError(err.message);
                            }}
                            onChange={onChangeHandler}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
